import React from 'react'
import type { Track } from 'types/track'
import {
  ALREADY_NESTED_TRACK,
  NESTED_INTO_ITSELF,
  NESTED_TRACK_ALREADY_IN_PLAYLIST,
  NESTED_TRACK_INTO_ANOTHER_TRACK,
  TOO_MANY_VERSIONS,
  VERSION_OF_MAIN_TRACK_ALREADY_IN_PLAYLIST,
} from '@disco/constants/Nesting'
import ModalDialog from 'components/ui/modal-dialog'
import Skeleton from 'components/ui/skeleton'

const getTrackNameWithLabel = (
  track: Track | null | undefined,
): React.ReactNode => {
  if (!track) return <Skeleton />
  return (
    <>
      <span>’</span>
      <b>
        {track.name}
        {track.nesting_label ? <span>{`(${track.nesting_label})`}</span> : null}
      </b>
      <span>’</span>
    </>
  )
}

type Props =
  | {
      isAwaiting?: boolean
      type: typeof ALREADY_NESTED_TRACK
      track: Track
      versionOf: Track
      newVersionOf: Track
      onProceed: (...args: Array<any>) => any
      onCancel: (...args: Array<any>) => any
    }
  | {
      isAwaiting?: boolean
      type: typeof NESTED_TRACK_INTO_ANOTHER_TRACK
      track: Track
      newVersionOf: Track
      onProceed: (...args: Array<any>) => any
      onCancel: (...args: Array<any>) => any
    }
  | {
      isAwaiting?: boolean
      type: typeof NESTED_TRACK_ALREADY_IN_PLAYLIST
      track: Track
      duplicateTrack: Track
      onProceed: (...args: Array<any>) => any
      onCancel: (...args: Array<any>) => any
    }
  | {
      isAwaiting?: boolean
      type: typeof VERSION_OF_MAIN_TRACK_ALREADY_IN_PLAYLIST
      track: Track
      duplicateTrack: Track
      onCancel: (...args: Array<any>) => any
    }
  | {
      isAwaiting?: boolean
      type: typeof TOO_MANY_VERSIONS
      onProceed: (...args: Array<any>) => any
    }
  | {
      isAwaiting?: boolean
      type: typeof NESTED_INTO_ITSELF
      onProceed: (...args: Array<any>) => any
    }

/**
 * Nesting validation modal component that handles various track nesting conflict scenarios
 * Displays different modal dialogs based on nesting validation errors with appropriate actions
 * Supports multiple nesting conflict types with contextual messaging and user options
 */
const NestingValidationModal = (props: Props): null | React.ReactNode => {
  switch (props.type) {
    case ALREADY_NESTED_TRACK:
      return (
        <ModalDialog
          title="Tracks can only be nested once"
          primaryAction={{
            caption: 'Remove and proceed',
            progressCaption: 'Proceeding',
            progressIndicator: props.isAwaiting,
            onClick: props.onProceed,
          }}
          onCancel={props.onCancel}
          status="warning"
          width="large"
        >
          <p>
            {getTrackNameWithLabel(props.track)} is already nested in{' '}
            {getTrackNameWithLabel(props.versionOf)}.{' '}
          </p>
          Would you like to remove {getTrackNameWithLabel(props.track)} as a
          version from {getTrackNameWithLabel(props.versionOf)} and nest it in{' '}
          {getTrackNameWithLabel(props.newVersionOf)}?
        </ModalDialog>
      )

    case NESTED_TRACK_INTO_ANOTHER_TRACK:
      return (
        <ModalDialog
          title="Tracks can only be nested once"
          primaryAction={{
            caption: 'Proceed',
            onClick: props.onProceed,
          }}
          onCancel={props.onCancel}
          status="warning"
          width="large"
        >
          {getTrackNameWithLabel(props.track)} has nested versions associated
          with it. If you nest it under{' '}
          {getTrackNameWithLabel(props.newVersionOf)},{' '}
          {getTrackNameWithLabel(props.track)} and all its versions will be
          nested under {getTrackNameWithLabel(props.newVersionOf)}.
        </ModalDialog>
      )

    case NESTED_TRACK_ALREADY_IN_PLAYLIST:
      return (
        <ModalDialog
          title="Tracks can only be included in a playlist once"
          primaryAction={{
            caption: 'OK',
            onClick: props.onProceed,
          }}
          onCancel={props.onCancel}
          status="warning"
          width="large"
        >
          {getTrackNameWithLabel(props.track)} is already included in this
          playlist as a nested version of{' '}
          {getTrackNameWithLabel(props.duplicateTrack)} and can’t be added
          again. If you’d like both versions to appear as top level tracks, turn
          off nesting for this playlist in the ‘Formats’ tab of the save pop up.
        </ModalDialog>
      )

    case VERSION_OF_MAIN_TRACK_ALREADY_IN_PLAYLIST:
      return (
        <ModalDialog
          title="Tracks can only be included in a playlist once"
          primaryAction={{
            caption: 'OK',
            onClick: props.onCancel,
          }}
          onCancel={props.onCancel}
          status="warning"
          width="large"
        >
          {getTrackNameWithLabel(props.track)} is a version of{' '}
          {getTrackNameWithLabel(props.duplicateTrack)} and has been removed as
          a top-level track in this playlist. If you’d like both versions to
          appear as top level tracks, turn off nesting for this playlist in the
          ’Formats’ tab of the save pop up.
        </ModalDialog>
      )

    case TOO_MANY_VERSIONS:
      return (
        <ModalDialog
          title="Nesting limit reached for this track!"
          primaryAction={{
            caption: 'OK',
            onClick: props.onProceed,
          }}
          status="warning"
          width="large"
        >
          You’ve reached the limit of nested versions for this track. The
          maximum number of nested versions for each track is 20 non-stem
          versions and 100 stems.
        </ModalDialog>
      )

    case NESTED_INTO_ITSELF:
      return (
        <ModalDialog
          title="Sorry! This track cannot be nested."
          primaryAction={{
            caption: 'OK',
            onClick: props.onProceed,
          }}
          status="warning"
          width="large"
        >
          We don’t allow a track to be nested into itself.
        </ModalDialog>
      )

    default:
      return null
  }
}

export default NestingValidationModal
